import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './header.css';
import { FaBars, FaSignOutAlt, FaBell, FaCalendarAlt, FaHashtag } from 'react-icons/fa';
import keycloakAPI from '../../keycloak/keycloakAPI';
import { UserContext } from '../../App';

const BASE = (process.env.REACT_APP_APIURL || 'http://localhost:4000').replace(/\/$/, '');
const AVATAR_COLORS = ['#c9960c','#a87c0a','#10b981','#d4a843','#ef4444','#f0c040'];
const colorFor = (name) => AVATAR_COLORS[(name || 'U').charCodeAt(0) % AVATAR_COLORS.length];

const timeSince = (ts) => {
  const diff = (Date.now() - new Date(ts)) / 1000;
  if (diff < 60)    return 'ahora';
  if (diff < 3600)  return `hace ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return new Date(ts).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
};

const stripHtml = (html = '') => html.replace(/<[^>]*>/g, '').slice(0, 80);

const canAccessCanal = (canal, user) => {
  if (canal.tipo === 'public') return true;
  if (user?.firstRole) return true;
  return (user?.roles || []).includes(canal.grupo_keycloak);
};

const POLL_MS    = 10_000;
const MSG_KEY    = (id) => `lmc_${id}`;         // localStorage key for last-message-check timestamp
const FALLBACK_TS = () => new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();

function Header({ toggleMenu }) {
  const user     = useContext(UserContext);
  const navigate = useNavigate();
  const bellRef  = useRef(null);

  const [mentoriaNotifs, setMentoriaNotifs] = useState([]);
  const [msgNotifs,      setMsgNotifs]      = useState([]);
  const [canalIds,       setCanalIds]       = useState([]);
  const [bellOpen,       setBellOpen]       = useState(false);

  const initials = user?.firstName
    ? (user.firstName[0] + (user.lastName?.[0] || '')).toUpperCase()
    : (user?.username || 'U').slice(0, 2).toUpperCase();

  const avatarSrc = user?.avatarUrl
    ? (user.avatarUrl.startsWith('/avatars') ? `${BASE}${user.avatarUrl}` : user.avatarUrl)
    : null;

  // ── Load accessible channels once ──────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    fetch(`${BASE}/canal`)
      .then(r => r.json())
      .then(canales => {
        const ids = canales.filter(c => canAccessCanal(c, user)).map(c => c.id);
        setCanalIds(ids);
      })
      .catch(() => {});
  }, [user?.id, user?.firstRole]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Poll both notification sources ─────────────────────────────────────────
  const poll = useCallback(async () => {
    if (!user?.id) return;

    // 1. Mentoria notifications
    try {
      const d = await fetch(`${BASE}/notificacion/${user.id}`).then(r => r.json());
      setMentoriaNotifs(Array.isArray(d) ? d : []);
    } catch {}

    // 2. Recent chat messages (only when we have channel IDs)
    if (canalIds.length > 0) {
      try {
        const since = localStorage.getItem(MSG_KEY(user.id)) || FALLBACK_TS();
        const qs = new URLSearchParams({
          since,
          canal_ids: canalIds.join(','),
          user_id: user.id,
        });
        const d = await fetch(`${BASE}/mensaje/recientes?${qs}`).then(r => r.json());
        setMsgNotifs(Array.isArray(d) ? d : []);
      } catch {}
    }
  }, [user?.id, canalIds]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    poll();
    const t = setInterval(poll, POLL_MS);

    // Repoll immediately when tab regains focus
    const onVisible = () => { if (!document.hidden) poll(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [poll]);

  // ── Close bell on outside click ─────────────────────────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── Bell interactions ───────────────────────────────────────────────────────
  const unread = mentoriaNotifs.filter(n => !n.leida).length + msgNotifs.length;

  const openBell = () => {
    const opening = !bellOpen;
    setBellOpen(opening);
    if (opening && user?.id) {
      // Mark mentoria notifications as read
      if (mentoriaNotifs.some(n => !n.leida)) {
        fetch(`${BASE}/notificacion/read-all/${user.id}`, { method: 'PUT' }).catch(() => {});
        setMentoriaNotifs(prev => prev.map(n => ({ ...n, leida: true })));
      }
      // Reset message check timestamp
      localStorage.setItem(MSG_KEY(user.id), new Date().toISOString());
      setMsgNotifs([]);
    }
  };

  // Combined list sorted by date, newest first
  const combined = [
    ...mentoriaNotifs.slice(0, 6).map(n => ({
      key:   `m-${n.id}`,
      type:  'mentoria',
      title: n.titulo,
      sub:   n.cuerpo,
      time:  n.created_at,
      unread: !n.leida,
      onClick: () => { setBellOpen(false); navigate('/calendario'); },
    })),
    ...msgNotifs.slice(0, 6).map(m => ({
      key:   `c-${m.id}`,
      type:  'mensaje',
      title: `#${m.canal_nombre}`,
      sub:   `${m.username}: ${stripHtml(m.body)}`,
      time:  m.created_at,
      unread: true,
      onClick: () => { setBellOpen(false); navigate('/chat'); },
    })),
  ].sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 10);

  return (
    <header className="header">
      <div className="header-content">
        <div className="header-left">
          <FaBars className="menu-icon" onClick={toggleMenu} />
          <div className="header-title">CloserAcademy</div>
        </div>

        {user ? (
          <div className="header-right">

            {/* ── Bell ── */}
            <div className="header-bell-wrap" ref={bellRef}>
              <button className="header-bell-btn" onClick={openBell} title="Notificaciones">
                <FaBell />
                {unread > 0 && (
                  <span className="header-bell-badge">{unread > 9 ? '9+' : unread}</span>
                )}
              </button>

              {bellOpen && (
                <div className="header-bell-dropdown">
                  <div className="header-bell-header">Notificaciones</div>
                  {combined.length === 0 ? (
                    <div className="header-bell-empty">Sin notificaciones nuevas</div>
                  ) : (
                    <div className="header-bell-list">
                      {combined.map(item => (
                        <div
                          key={item.key}
                          className={`header-bell-item ${item.unread ? 'header-bell-item--unread' : ''}`}
                          onClick={item.onClick}
                        >
                          {item.type === 'mentoria'
                            ? <FaCalendarAlt className="header-bell-item-icon" />
                            : <FaHashtag className="header-bell-item-icon header-bell-item-icon--msg" />
                          }
                          <div className="header-bell-item-body">
                            <div className="header-bell-item-title">{item.title}</div>
                            <div className="header-bell-item-sub">{item.sub}</div>
                            <div className="header-bell-item-time">{timeSince(item.time)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Profile ── */}
            <Link to="/perfil" className="header-profile-link">
              <div
                className="header-avatar"
                style={{ background: avatarSrc ? 'transparent' : colorFor(user.username) }}
              >
                {avatarSrc
                  ? <img src={avatarSrc} alt="avatar" className="header-avatar-img" />
                  : initials
                }
              </div>
              <span className="header-username">{user.firstName || user.username}</span>
            </Link>

            <button className="header-logout-btn" onClick={() => keycloakAPI.logout()} title="Cerrar sesión">
              <FaSignOutAlt />
            </button>
          </div>
        ) : (
          <div className="header-right">Cargando...</div>
        )}
      </div>
    </header>
  );
}

export default Header;
